import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
// Constants - Functions
import { WEB_SERVICE_MIMS, pathologies } from "../../config/constants";
import { passwordValidation, dateValidation, HeightValidation } from "../../config/functions";
// Components
import Loader from "../loader";
import Axios from "../../config/axios";
import { Form } from "./styles";
import PatientNavigation from "../patientNavigation";
import ActionsManagement from "./sections/actionsManagement";
import Title from "../title";
import PersonalInformation from "./sections/personalInformation";

const EditPatient = () => {
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [gender, setGender] = useState("");
  const [height, setHeight] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [gameExperience, setGameExperience] = useState("");
  const [computerExperience, setComputerExperience] = useState("");
  const [otherInfo, setOtherInfo] = useState("");
  const [patientVersion, setPatientVersion] = useState(0);
  const [pathology, setPathology] = useState("");
  const [pareticSide, setPareticSide] = useState("LEFT");
  const [dateOfStroke, setDateOfStroke] = useState("");
  const [appsArray, setAppsArray] = useState([]);
  const [personalInfo, setPersonalInfo] = useState({});
  const [pathologyInfo, setPathologyInfo] = useState({});
  const [appsInfo] = useState([]);
  // handle errors
  const [passwordError, setPasswordError] = useState(false);
  const [genderError, setGenderError] = useState(false);
  const [heightError, setHeightError] = useState(false);
  const [dateOfBirthError, setDateOfBirthError] = useState(false);

  const [loading, setLoading] = useState(true);
  const [disabledInputs, setDisabledInputs] = useState(true);
  const [isRechargeComponent, setIsRechargeComponent] = useState(false);

  const { patientId } = useParams();
  const [t] = useTranslation("global");

  // Fetch patient data
  useEffect(() => {
    const URL = `${WEB_SERVICE_MIMS}/patients/fetch/${patientId}`;
    Axios("get", URL)
      .then(({ data }) => {
        setUser(data.PATIENT_USER);
        setGender(data.GENDER);
        setHeight(data.PTN_HEIGHT_CM);
        setDateOfBirth(data.BIRTH_DATE);
        data.VIDEOGAME_EXP === null ? setGameExperience("") : setGameExperience(data.VIDEOGAME_EXP);
        data.COMPUTER_EXP === null ? setComputerExperience("") : setComputerExperience(data.COMPUTER_EXP);
        data.COMMENTS === null ? setOtherInfo("") : setOtherInfo(data.COMMENTS);
        setPatientVersion(Number(data.VERSION) + 1);
        setPathology(Number(data.PATHOLOGY));
        if (data.PARETIC_SIDE !== "NONE") setPareticSide(data.PARETIC_SIDE);
        if (Number(data.PATHOLOGY) === pathologies.strokeId) {
          setDateOfStroke(data.STROKE_DATE);
        }
        setAppsArray(data.SOFTWARE_ID);
        setPathologyInfo({
          AFFECTATION_ID: pathologies.amputationId,
        });
        setLoading(false);
      })
      .catch((error) => {
        console.log("url error: ", error);
      });
  }, [patientId]);

  const renderPatientNavigation = () => {
    return <PatientNavigation patientId={patientId} section="profile" rechargeComponent={isRechargeComponent} />;
  };

  const renderActionsManagement = () => {
    return (
      <ActionsManagement
        handleSubmit={handleSubmit}
        disabledInputs={disabledInputs}
        setDisabledInputs={setDisabledInputs}
        patientId={patientId}
        patientVersion={patientVersion}
      />
    );
  };

  const renderFormSections = () => {
    return (
      <>
        <PersonalInformation
          disabledInputs={disabledInputs}
          user={user}
          password={password}
          setPassword={setPassword}
          passwordError={passwordError}
          setPasswordError={setPasswordError}
          gender={gender}
          setGender={setGender}
          genderError={genderError}
          setGenderError={setGenderError}
          height={height}
          setHeight={setHeight}
          heightError={heightError}
          setHeightError={setHeightError}
          dateOfBirth={dateOfBirth}
          setDateOfBirth={setDateOfBirth}
          dateOfBirthError={dateOfBirthError}
          setDateOfBirthError={setDateOfBirthError}
          gameExperience={gameExperience}
          setGameExperience={setGameExperience}
          computerExperience={computerExperience}
          setComputerExperience={setComputerExperience}
          otherInfo={otherInfo}
          setOtherInfo={setOtherInfo}
          pathology={pathology}
          pareticSide={pareticSide}
          setPareticSide={setPareticSide}
          patientVersion={patientVersion}
          setPersonalInfo={setPersonalInfo}
        />
      </>
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (
      gender === "" ||
      height === "" ||
      pathology === "" ||
      dateOfBirth === "" ||
      (Number(pathology) === pathologies.strokeId && dateOfStroke === "")
    ) {
      if (password !== "" && !passwordValidation(password)) setPasswordError(true);
      if (gender === "") setGenderError(true);
      if (height === "") setHeightError(true);
      if (dateOfBirth === "") setDateOfBirthError(true);
      return toast.error(t("profileMessage.emptyFieldsMessage"));
    }
    if (password !== "" && !passwordValidation(password)) {
      setPasswordError(true);
      return toast.error(t("profileMessage.passwordMessage"));
    }
    if (!HeightValidation(height)) {
      setHeightError(true);
      return toast.error(t("profileMessage.heightMessage"));
    }
    if (!dateValidation(dateOfBirth)) {
      setDateOfBirthError(true);
      return toast.error(t("profileMessage.birthDateMessage"));
    }
    if (appsArray.length === 0) {
      return toast.error(t("profileMessage.applicationsMessage"));
    }
    let data = {
      appsInfo,
      pathologyInfo,
      personalInfo,
    };
    if (data.personalInfo.VERSION) {
      setPatientVersion(patientVersion + 1);
    }
    const URL = `${WEB_SERVICE_MIMS}/patients/update/${patientId}`;
    Axios("put", URL, data)
      .then(({ data }) => {
        if (data.updatedPatient) {
          setDisabledInputs(true);
          setIsRechargeComponent(!isRechargeComponent);
          toast.success(t("editPatient.patientUpdatedMessage"));
        } else {
          toast.error(t("profileMessage.sendDataMessage"));
        }
      })
      .catch((error) => {
        toast.error(t("profileMessage.sendDataMessage"));
        console.log("url error: ", error);
      });
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          {renderPatientNavigation()}
          {renderActionsManagement()}
          <Title title={t("editPatient.patientDataTitle")} marginBottom="10px" />
          <Form onSubmit={handleSubmit}>{renderFormSections()}</Form>
        </>
      )}
    </>
  );
};

export default EditPatient;
